/**
 * Format ▸ Headers & footers ▸ More options.
 *
 * THE SETTINGS ARE THE RENDITION'S as much as the editor's: where the running header sits on a
 * printed sheet, and whether page one carries it. That is why they live on the document (see
 * pageSetup in PageElements) rather than in this browser -- a margin that existed only on screen
 * would mean the signed PDF was laid out differently from the page the author approved.
 *
 * ONE CONTROL IS DISABLED AND SAYS SO. "Different odd & even" needs a SECOND header and footer in
 * the document -- separate content for the even pages, not a mirrored copy of the odd one -- and
 * that is a change to the schema rather than a setting. Offering a checkbox that quietly did
 * something else would be worse than the greyed row.
 */
import React from "react";
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";

import { DEFAULT_PAGE_SETUP, PageSetup } from "./PageElements";
import { ODD_EVEN_PAGES } from "./unbuilt";

const ODD_EVEN_REASON = ODD_EVEN_PAGES;

/** A typed field's value as inches, or the default when it is not a number at all. */
function _inches(value: any, fallback: number) {
    const parsed = parseFloat(String(value));
    return Number.isFinite(parsed) ? Math.min(4, Math.max(0, parsed)) : fallback;
}

export default function PageSetupDialog({ open, value, onClose, onApply }: {
    open: boolean;
    value: PageSetup;
    onClose: () => void;
    onApply: (next: PageSetup) => void;
}) {
    // HELD LOCALLY UNTIL APPLY. The dialog has a Cancel, so it has to be possible to change your
    // mind -- writing each keystroke into the document would make Cancel a lie.
    const [draft, setDraft] = React.useState<PageSetup>(value);
    React.useEffect(() => { if (open) setDraft(value); }, [open, value]);

    const number = (name: "headerMargin" | "footerMargin"
                        | "marginTop" | "marginBottom" | "marginLeft" | "marginRight") => ({
        value: String(draft[name]),
        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft({ ...draft, [name]: event.target.value as any }),
        onBlur: () => {
            const parsed = parseFloat(String(draft[name]));
            // A BLANK OR A TYPO GOES BACK TO WHAT THAT FIELD STARTED AS, not to one fixed number:
            // 0.5 is right for a header offset and wrong for a page margin, and a field that
            // silently becomes half an inch is a page the author did not ask for.
            const fallback = (DEFAULT_PAGE_SETUP as any)[name];
            setDraft({ ...draft, [name]: Number.isFinite(parsed)
                ? Math.min(4, Math.max(0, parsed)) : fallback });
        },
        size: "small" as const,
        fullWidth: true,
        slotProps: { htmlInput: { inputMode: "decimal" as const,
                                  style: { fontSize: 15, padding: "10px 12px" } } },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
                slotProps={{ paper: { sx: { borderRadius: 2, px: 1 } } }}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 400, pb: 1 }}>Page setup</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Orientation</Typography>
                {/* TWO BUTTONS, NOT A DROPDOWN. There are exactly two answers and both fit on a
                    line; a select would hide one of them behind a click. */}
                <ToggleButtonGroup exclusive size="small" sx={{ mb: 3 }}
                                   value={draft.orientation === "landscape" ? "landscape" : "portrait"}
                                   onChange={(_, next) => next
                                       && setDraft({ ...draft, orientation: next })}>
                    <ToggleButton value="portrait" sx={{ textTransform: "none", px: 2 }}>Portrait</ToggleButton>
                    <ToggleButton value="landscape" sx={{ textTransform: "none", px: 2 }}>Landscape</ToggleButton>
                </ToggleButtonGroup>

                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>Page margins (inches)</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
                    {([["marginTop", "Top"], ["marginBottom", "Bottom"],
                       ["marginLeft", "Left"], ["marginRight", "Right"]] as const).map(([name, label]) => (
                        <Box key={name}>
                            <Typography sx={{ fontSize: 13, color: "#525968", mb: 0.5 }}>{label}</Typography>
                            <TextField {...number(name)}/>
                        </Box>
                    ))}
                </Box>
                {/* WHAT THE NUMBERS ACTUALLY MOVE. The ruler, the page-break preview and the PDF
                    all measure against these, so saying so here is cheaper than somebody finding
                    out by printing. */}
                <Typography sx={{ fontSize: 12, color: "#525968", mb: 3 }}>
                    The ruler, the page breaks and the PDF all measure from these.
                </Typography>

                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>Header &amp; footer</Typography>

                <Typography sx={{ fontSize: 13, color: "#525968", mb: 0.5 }}>
                    Header (inches from top)
                </Typography>
                <TextField {...number("headerMargin")} sx={{ mb: 2 }}/>

                <Typography sx={{ fontSize: 13, color: "#525968", mb: 0.5 }}>
                    Footer (inches from bottom)
                </Typography>
                <TextField {...number("footerMargin")} sx={{ mb: 3 }}/>

                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Columns</Typography>
                {/* THREE ANSWERS, SAME SHAPE AS ORIENTATION. The text flows down one column and on
                    into the next, and on to the following page -- it is not a side-by-side layout,
                    which is a table and already renders. */}
                <ToggleButtonGroup exclusive size="small" sx={{ mb: 1 }}
                                   value={Number(draft.columns) === 2 || Number(draft.columns) === 3
                                       ? Number(draft.columns) : 1}
                                   onChange={(_, next) => next
                                       && setDraft({ ...draft, columns: next })}>
                    <ToggleButton value={1} sx={{ textTransform: "none", px: 2 }}>One</ToggleButton>
                    <ToggleButton value={2} sx={{ textTransform: "none", px: 2 }}>Two</ToggleButton>
                    <ToggleButton value={3} sx={{ textTransform: "none", px: 2 }}>Three</ToggleButton>
                </ToggleButtonGroup>
                {/* SAID HERE, WHERE THE CHOICE IS MADE, rather than discovered afterwards. The page
                    preview cannot show a column break: it simulates one by pushing a block DOWN,
                    and the next column is sideways. The PDF is laid out properly either way. */}
                <Typography sx={{ fontSize: 12, color: "#525968", mb: 3 }}>
                    {Number(draft.columns) > 1
                        ? "Page breaks are not previewed in a multi-column document \u2014 the "
                          + "text runs on as one flow on screen. The PDF breaks the pages."
                        : "Two or three columns flow the text down one and on into the next."}
                </Typography>

                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>Layout</Typography>

                <Box>
                    <FormControlLabel
                        control={<Checkbox checked={draft.differentFirstPage}
                                           onChange={(e) => setDraft({ ...draft,
                                                                       differentFirstPage: e.target.checked })}/>}
                        label={<Box>
                            <Typography sx={{ fontSize: 14 }}>Different first page</Typography>
                            <Typography sx={{ fontSize: 12, color: "#525968" }}>
                                The running header and footer are left off page one.
                            </Typography>
                        </Box>}
                        sx={{ alignItems: "flex-start", mb: 1 }}/>
                </Box>

                {/* A DISABLED CONTROL STILL HAS TO SAY WHY, and a disabled checkbox swallows hover
                    -- so the tooltip goes on a span around it, the same arrangement the menus use. */}
                <Tooltip title={ODD_EVEN_REASON} placement="bottom-start">
                    <span>
                        <FormControlLabel disabled
                            control={<Checkbox checked={false} disabled/>}
                            label={<Typography sx={{ fontSize: 14 }}>Different odd &amp; even</Typography>}/>
                    </span>
                </Tooltip>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 14 }}>Cancel</Button>
                <Button variant="contained" disableElevation
                        onClick={() => onApply({
                            headerMargin: Number(draft.headerMargin) || 0,
                            footerMargin: Number(draft.footerMargin) || 0,
                            differentFirstPage: !!draft.differentFirstPage,
                            orientation: draft.orientation === "landscape" ? "landscape" : "portrait",
                            // `|| 0` WOULD BE WRONG HERE. A margin of zero is a real answer, so an
                            // unparseable field falls back to this document's default rather than
                            // to nothing -- the blur handler has already clamped anything typeable.
                            marginTop: _inches(draft.marginTop, DEFAULT_PAGE_SETUP.marginTop),
                            marginBottom: _inches(draft.marginBottom, DEFAULT_PAGE_SETUP.marginBottom),
                            marginLeft: _inches(draft.marginLeft, DEFAULT_PAGE_SETUP.marginLeft),
                            marginRight: _inches(draft.marginRight, DEFAULT_PAGE_SETUP.marginRight),
                            columns: Number(draft.columns) === 2 || Number(draft.columns) === 3
                                ? Number(draft.columns) : 1,
                        })}
                        sx={{ textTransform: "none", fontSize: 14, borderRadius: 5, px: 3 }}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}
